import ConsoleTools from '@/components/ConsoleTools'
import { useState, type FormEvent } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { ArrowRight, Check, Eye, EyeOff, ShieldCheck } from 'lucide-react'
import { authApi } from '@/api/auth'
import { errorMessage } from '@/api/client'
import { useAuthStore } from '@/store/auth'

export default function LoginPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const login = useAuthStore((s) => s.login)
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [loginPhase, setLoginPhase] = useState<'idle' | 'orb' | 'success'>('idle')
  const [showPassword, setShowPassword] = useState(false)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)

  const mutation = useMutation({
    mutationFn: authApi.login,
    onSuccess: async (res) => {
      if (res.code === 0 && res.data) {
        setLoginPhase('orb')
        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
        if (!reduced) await new Promise(resolve => setTimeout(resolve, 400))
        setLoginPhase('success')
        if (!reduced) await new Promise(resolve => setTimeout(resolve, 550))
        login(res.data.token, res.data.admin)
        const from =
          (location.state as { from?: { pathname: string } } | null)?.from
            ?.pathname || '/dashboard'
        const role = res.data.admin.role
        const permitted =
          (from !== '/audit-logs' || role === 'super_admin') &&
          (from !== '/recharges' || role !== 'operator')
        navigate(permitted ? from : '/dashboard', { replace: true })
      } else {
        setErrorMsg(res.message || '登录失败')
      }
    },
    onError: (err: unknown) => {
      const msg = errorMessage(err)
      setErrorMsg(msg)
    },
  })

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    if (mutation.isPending || loginPhase !== 'idle') return
    setErrorMsg(null)
    mutation.mutate({ username, password })
  }

  return (
    <div className="relative flex min-h-dvh flex-col items-center justify-center overflow-hidden bg-slate-50 px-5 py-10">
      <ConsoleTools />
      <div aria-hidden="true" className="pointer-events-none absolute -top-48 left-1/2 h-[600px] w-[800px] -translate-x-1/2 rounded-full bg-sky-100/70 blur-3xl" />
      <div aria-hidden="true" className="pointer-events-none absolute -bottom-56 -right-32 h-[520px] w-[520px] rounded-full bg-blue-100/50 blur-3xl" />
      <div className="relative w-full max-w-[420px]">
        <div className="mb-7 flex items-center justify-center gap-2.5">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-brand-600 text-white shadow-sm"><ShieldCheck size={21} /></div>
          <span className="text-xl font-semibold tracking-tight text-slate-900">PPNew <span className="ml-1 text-sm font-normal text-slate-500">管理后台</span></span>
        </div>
        <form onSubmit={handleSubmit} className="login-card rounded-3xl border border-white bg-white p-7 shadow-[0_16px_60px_-20px_rgba(15,23,42,0.2)] sm:p-9">
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">欢迎回来</h1>
          <p className="mb-7 mt-2 text-sm text-slate-500">登录你的管理账号，开始今天的工作。</p>
          <label htmlFor="username" className="mb-2 block text-sm font-medium text-slate-700">账号</label>
          <input id="username" autoComplete="username" type="text" value={username} onChange={(e) => setUsername(e.target.value)} required autoFocus placeholder="请输入管理账号" className="mb-5 h-12 w-full rounded-xl border border-slate-200 bg-slate-50/60 px-4 text-sm outline-none transition focus:border-brand-500 focus:bg-white focus:ring-4 focus:ring-brand-50" />
          <label htmlFor="password" className="mb-2 block text-sm font-medium text-slate-700">密码</label>
          <div className="relative mb-6">
            <input id="password" autoComplete="current-password" type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} required placeholder="请输入密码" className="h-12 w-full rounded-xl border border-slate-200 bg-slate-50/60 pl-4 pr-12 text-sm outline-none transition focus:border-brand-500 focus:bg-white focus:ring-4 focus:ring-brand-50" />
            <button type="button" aria-label={showPassword ? '隐藏密码' : '显示密码'} aria-pressed={showPassword} onClick={() => setShowPassword(!showPassword)} className="absolute right-2 top-1/2 -translate-y-1/2 rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-600">{showPassword ? <EyeOff size={18} /> : <Eye size={18} />}</button>
          </div>
          {errorMsg && <div role="alert" className="mb-4 rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700">{errorMsg}</div>}
          <button type="submit" disabled={mutation.isPending || loginPhase !== 'idle'} className={`login-morph ${loginPhase} flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-brand-600 text-sm font-medium text-white shadow-sm`} aria-label={loginPhase === 'success' ? '登录成功' : '登录'}>
            {loginPhase === 'success' ? <Check className="success-check" size={25}/> : loginPhase === 'orb' ? <span className="orb-core"/> : <>{mutation.isPending ? '验证中…' : '登录'}{!mutation.isPending && <ArrowRight size={17}/>}</>}
          </button>
        </form>
        <p className="mt-6 text-center text-xs text-slate-400">PPNew · 管理工作台</p>
      </div>
    </div>
  )
}
